/**
 * Bosh sahifa — docs/SPEC.md §home.
 *
 * Uchta ro'yxat va uchalasi ham boshqa savolga javob beradi:
 *
 *   **Hafta tanlovi** — "shu hafta qaysi room ahamiyatli bo'ldi"
 *   (haftalik ball, egasi qarori 2026-08-12).
 *
 *   **Hozir efirda** — `score` bo'yicha, ya'ni "hozir qizigan".
 *   Sof tinglovchi soni EMAS: u winner-take-all beradi.
 *
 *   **Yangi roomlar** — id bo'yicha. Usiz yangi host TOPga hech qachon
 *   chiqa olmasdi va birinchi efirini bo'sh xonada o'tkazardi (D-006).
 *
 * O'rin yorlig'i KASR EMAS, MA'NO (qoida 19): `3 O'RIN BO'SH`, kasr
 * `9/12` emas — foydalanuvchi ayirishi kerak bo'lardi.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { uz } from '../i18n/uz';
import { useMe } from '../lib/api/useMe';
import { useFeaturedRoom, useRoomList, type RoomRow } from '../lib/api/useRoom';
import { useWallet } from '../lib/api/useWallet';
import { useConfig } from '../lib/config/useConfig';
import { fmt, seatsLabel } from '../lib/format';
import { Avatar, CTAButton, Chip, Icon, Toast } from '../ui';
import { useToast } from '../ui/useToast';
import './screens.css';

/* Kategoriyalar `create` ekranidagi MAVZULAR bilan bir xil — ular
 * ajralib ketsa foydalanuvchi tanlagan mavzu filtrda ko'rinmay qolardi. */
const CATEGORIES = ['Do‘stlashuv', 'Musiqa', 'Til', 'O‘yin', 'Podkast', 'Erkin'] as const;

export function Home() {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const wallet = useWallet();
  const config = useConfig();

  // Flag o'chiq bo'lsa room endpointlari 404 qaytaradi (qoida 23) —
  // so'rovni umuman yubormaymiz (audit T-032).
  const roomsOn = config.data?.flags['rooms_enabled'] === true;

  const top = useRoomList('top', roomsOn);
  const fresh = useRoomList('new', roomsOn);
  const featured = useFeaturedRoom(roomsOn);

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string | null>(null);

  const liveRooms = top.data?.items ?? [];
  // Yangi ro'yxatdan TOPda ko'ringanlarini chiqarib tashlaymiz — bir
  // room ikki bo'limda turishi ro'yxatni takrorga aylantirardi.
  const shown = new Set(liveRooms.map((room) => room.id));
  const freshRooms = (fresh.data?.items ?? []).filter((room) => !shown.has(room.id));

  const match = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (room: RoomRow) =>
      (category === null || room.topic === category) &&
      (term === '' ||
        room.title.toLowerCase().includes(term) ||
        room.host_name.toLowerCase().includes(term));
  }, [query, category]);

  const visibleLive = liveRooms.filter(match);
  const visibleFresh = freshRooms.filter(match);
  const filtering = query.trim() !== '' || category !== null;
  const featuredRoom = featured.data ?? null;

  function open(room: RoomRow): void {
    if (room.type === 'password') {
      // Parolli roomga kirish alohida qadam (parol so'rovi).
      toast.show(uz.home.locked);
      return;
    }
    navigate(`/room/${room.id}`);
  }

  return (
    <div className="screen">
      {toast.message && <Toast message={toast.message} />}

      <header className="screen-bar">
        <button type="button" className="icon-btn" onClick={() => navigate('/profile')}>
          <Avatar
            letter={(me.data?.first_name ?? '?').slice(0, 1)}
            paletteIdx={me.data?.palette_idx ?? 0}
            size={34}
          />
        </button>
        <h1 className="screen-bar__title">{uz.home.greeting(me.data?.first_name ?? '')}</h1>

        {/* Balans chipi — §6 dagi 4 ta blur yuzasidan biri va `shop` ga
            yagona yo'l (SPEC §home 1-band). */}
        <button type="button" className="balance-chip blur-chip" onClick={() => navigate('/shop')}>
          <Icon name="gem" size={13} />
          <span className="tnum">{fmt(wallet.data?.gems ?? 0)}</span>
        </button>
      </header>

      {!config.isLoading && !roomsOn ? (
        /* Roomlar hali ochilmagan — bosh sahifa BUZILMAYDI, 1v1 ga
           yo'naltiradi (audit T-032). */
        <div className="empty" style={{ marginTop: 40 }}>
          <div>{uz.home.roomsSoon}</div>
          <div style={{ marginTop: 18 }}>
            <CTAButton variant="match" onClick={() => navigate('/match')}>
              {uz.nav.match}
            </CTAButton>
          </div>
        </div>
      ) : top.isError ? (
        <div className="panel error" style={{ marginTop: 20 }}>
          <div>{uz.home.loadFailed}</div>
          <div style={{ marginTop: 12 }}>
            <CTAButton variant="ghost" size="sm" onClick={() => void top.refetch()}>
              {uz.common.retry}
            </CTAButton>
          </div>
        </div>
      ) : top.isLoading ? (
        <div className="muted" style={{ marginTop: 20 }}>
          {uz.common.loading}
        </div>
      ) : (
        <>
          <div className="home__search">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={uz.home.searchPlaceholder}
            />
            <button type="button" className="cta-round" onClick={() => navigate('/create')}>
              <Icon name="plus" size={18} />
            </button>
          </div>

          <div className="chip-row" style={{ marginTop: 12 }}>
            <Chip
              className="chip--lang"
              active={category === null}
              onClick={() => setCategory(null)}
            >
              {uz.home.categoryAll}
            </Chip>
            {CATEGORIES.map((value) => (
              <Chip
                key={value}
                className="chip--lang"
                active={category === value}
                onClick={() => setCategory(value)}
              >
                {value}
              </Chip>
            ))}
          </div>

          {/* Hafta tanlovi. Filtr yoqilganda KO'RSATILMAYDI: u ro'yxatga
              tegishli emas va filtrlangan natijada chalkashtirardi. */}
          {featuredRoom && !filtering && (
            <button type="button" className="featured" onClick={() => open(featuredRoom)}>
              <span className="featured__label">
                <span className="live-dot" />
                {uz.home.live}
              </span>
              <span className="featured__title">{featuredRoom.title}</span>
              <span className="featured__meta tnum">
                {`${featuredRoom.host_name} · ${featuredRoom.listeners}`}
              </span>
            </button>
          )}

          {visibleLive.length === 0 && visibleFresh.length === 0 ? (
            /* Bo'sh holat matnsiz qolmaydi (qoida 19). */
            <div className="empty" style={{ marginTop: 40 }}>
              <div>{filtering ? uz.home.noMatch : uz.home.empty}</div>
              {!filtering && (
                <div style={{ marginTop: 18 }}>
                  <CTAButton variant="match" onClick={() => navigate('/create')}>
                    {uz.home.create}
                  </CTAButton>
                </div>
              )}
            </div>
          ) : (
            <>
              {visibleLive.length > 0 && (
                <>
                  <div className="section-head">
                    <span className="section-head__title">{uz.home.liveNow}</span>
                    <span className="section-head__rule" />
                    <span className="section-head__count tnum">{visibleLive.length}</span>
                  </div>
                  {visibleLive.map((room) => (
                    <RoomCard key={room.id} room={room} onOpen={open} />
                  ))}
                </>
              )}

              {visibleFresh.length > 0 && (
                <>
                  <div className="section-head">
                    <span className="section-head__title">{uz.home.fresh}</span>
                    <span className="section-head__rule" />
                    <span className="section-head__count tnum">{visibleFresh.length}</span>
                  </div>
                  {visibleFresh.map((room) => (
                    <RoomCard key={room.id} room={room} onOpen={open} />
                  ))}
                </>
              )}
            </>
          )}
        </>
      )}

      <div className="screen-tail" />
    </div>
  );
}

function RoomCard({ room, onOpen }: { room: RoomRow; onOpen: (room: RoomRow) => void }) {
  const free = room.capacity - room.speakers;

  return (
    <button type="button" className="room-card" onClick={() => onOpen(room)}>
      <span className="room-card__tile">{room.topic.slice(0, 1)}</span>

      <span className="room-card__body">
        <span className="room-card__title">
          {room.title}
          {room.type === 'password' && <Icon name="lock" size={12} />}
        </span>
        <span className="room-card__host">{`${room.host_name} · ${room.topic}`}</span>
      </span>

      <span className="room-card__side">
        <span className="room-card__count tnum">{room.listeners}</span>
        {/* Kasr emas, ma'no (qoida 19). */}
        <span className="room-card__seats">{seatsLabel(free)}</span>
      </span>
    </button>
  );
}
